import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { take } from 'rxjs/operators';
import { Conversion } from 'src/app/models/conversion.model';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { SharedService } from 'src/app/shared/services/shared.service';
import { User } from 'src/app/models/users.model';
import { ActivatedRoute } from '@angular/router';
import { ConversionDetailsComponent } from 'src/app/shared/ui/conversion-details/conversion-details.component';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { EnhancedInfiniteList } from 'src/app/models/infinite-list.model';
import { DialogComponent } from 'src/app/shared/ui/dialog/dialog.component';
import { ConversionService } from 'src/app/shared/services/conversion.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
const firestore = firebase.firestore;

interface BatchData {
  isBatch: boolean;
  isBatchStart: boolean;
  isBatchEnd: boolean;
}

type EnhancedConversion = Conversion & BatchData;

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
})
export class HistoryComponent implements OnInit {
  infiniteList: EnhancedInfiniteList<Conversion, EnhancedConversion> = {
    data: [],
    enhancedData: [],
    placeholders: [],
    loading: false,
    theEnd: false,
    pageSize: 25,
  };

  user: User;
  loading = true;

  constructor(
    private ss: SharedService,
    private activatedRoute: ActivatedRoute,
    private conversionService: ConversionService,
    private db: AngularFirestore,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private http: HttpClient,
  ) {}

  async ngOnInit() {
    this.user = await this.ss.getUser();

    const conversionUid = this.activatedRoute.snapshot.paramMap.get('conversionUid');
    if (conversionUid) {
      const conversion = (
        await firestore().collection(`files`).where('uid', '==', conversionUid).where('userUid', '==', this.user.uid).get()
      ).docs[0].data() as Conversion;
      this.viewDetailsClicked(conversion);
    }
  }

  getBatchData(conversion: Conversion, index: number): BatchData {
    const prevConversion = this.infiniteList.data[index - 1];
    const nextConversion = this.infiniteList.data[index + 1];

    const isBatch = prevConversion?.batchUid === conversion.batchUid || nextConversion?.batchUid === conversion.batchUid;
    const isBatchStart = isBatch && prevConversion?.batchUid !== conversion.batchUid;
    const isBatchEnd = isBatch && nextConversion?.batchUid !== conversion.batchUid;

    return { isBatch, isBatchStart, isBatchEnd };
  }

  getBatch(startAfter: Date | firebase.firestore.Timestamp) {
    return this.db
      .collection<Conversion>('files', (ref) =>
        ref.where('userUid', '==', this.user.uid).orderBy('uploadDate', 'desc').startAfter(startAfter).limit(this.infiniteList.pageSize),
      )
      .snapshotChanges();
  }

  /**
   * Keys rows by conversion uid so loading the next page doesn't destroy and
   * recreate every existing row component (which made row actions flicker).
   */
  trackByUid(index: number, item: Conversion): string | number {
    return item?.uid || index;
  }

  loadNext(listData: EnhancedInfiniteList<Conversion, EnhancedConversion>) {
    if (listData.loading || listData.theEnd) {
      return;
    }

    listData.loading = true;

    this.getBatch(listData.data[listData.data.length - 1]?.uploadDate ?? new Date('3000')).subscribe((next) => {
      if (next[0]?.payload.doc.metadata.fromCache) {
        return;
      }
      console.log('Load Next');
      this.loading = false;
      if (next.length < this.infiniteList.pageSize) {
        this.infiniteList.theEnd = true;
      }
      next.forEach((_data) => {
        const data = _data.payload.doc.data();
        const index = listData.data.findIndex((a) => a.uid === data.uid);
        if (data.deleted) {
          // Remove from list if it was deleted
          if (index > -1) listData.data.splice(index, 1);
          return;
        }
        if (index > -1) {
          listData.data[index] = data;
        } else {
          listData.data.push(data);
        }
      });

      // update enhancedData
      listData.enhancedData = [];
      listData.enhancedData = listData.data.map((data, i) => {
        return { ...data, ...this.getBatchData(data, i) };
      });

      listData.loading = false;
    });
  }

  viewDetailsClicked(c: Conversion) {
    this.dialogService.open(ConversionDetailsComponent, {
      context: {
        conversion: c,
      },
    });
  }

  async deleteClicked(c: Conversion) {
    const result = await this.dialogService
      .open(DialogComponent, {
        context: {
          title: `Delete Conversion Data`,
          body: `Are you sure you want to delete this conversion - ${c.fileName}?
          This will remove the original PDF and parsed data from the SwiftCSV platform permanently.`,
          cancel: 'Back',
          confirm: `Delete`,
          confirmClickDelay: 500,
          status: 'danger',
        },
      })
      .onClose.pipe(take(1))
      .toPromise();

    if (result === 'confirm') {
      await this.conversionService.deleteConversion(c, this.user);
      this.toastr.success(`Removed conversion data`, 'Deleted', { icon: 'trash-outline' });
    }
  }

  async bulkDownloadClicked(type: 'csv' | 'excel', conversion: EnhancedConversion) {
    const toast = this.toastr.info(`Generating bulk ${type} file`, 'Generating File', { icon: 'download-outline', duration: 10000 });

    try {
      const res = (await this.http
        .post(`${environment.functionsRootUrl}/dp-generateBulkParsedFiles/`, {
          conversionBatchUid: conversion.batchUid,
        })
        .toPromise()) as { csvUrl: string; xlsxUrl: string };

      const link = type === 'csv' ? res.csvUrl : res.xlsxUrl;

      const linkEl = document.createElement('a');
      linkEl.href = link;
      linkEl.click();
      linkEl.remove();
    } catch (error) {
      this.toastr.warning(error.message || error, 'Something went wrong');
    }
    toast.close();
  }
}
